import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check } from 'lucide-react';
import type { GithubRepoDto, ProjectDto, WorkspaceEntitlementsDto } from '@aiai/shared';
import {
  createProject,
  fetchGithubRepos,
  fetchProjects,
  fetchWorkspaceEntitlements,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { useOrg } from '../org/useOrg';
import { Modal } from './Modal';
import { Spinner } from './Spinner';
import { WorkspaceSelectField } from './WorkspaceSelectField';

export function NewProjectModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (project: ProjectDto) => void;
}) {
  const queryClient = useQueryClient();
  const { activeOrg } = useOrg();
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<GithubRepoDto | null>(null);

  // Repo projects are a plan entitlement (pro/byo); the basic plan is
  // prototypes-only. The server enforces this too (`assertFeature(orgId, 'repoProjects')`),
  // but gating the picker here avoids offering an action that will only fail.
  const { data: entitlements } = useQuery<WorkspaceEntitlementsDto>({
    queryKey: ['orgEntitlements', activeOrg?.id],
    queryFn: () => fetchWorkspaceEntitlements(activeOrg!.id),
    enabled: open && Boolean(activeOrg?.id),
  });
  const repoProjectsBlocked = entitlements?.features.repoProjects.enabled === false;

  // Load the user's repos via the configured token; only while the modal is open.
  const reposQuery = useQuery<GithubRepoDto[]>({
    queryKey: qk.githubRepos,
    queryFn: fetchGithubRepos,
    enabled: open,
    retry: false,
    staleTime: 60_000,
  });

  // Repos already added as projects — shown disabled so a repo can't be added twice.
  const projectsQuery = useQuery<ProjectDto[]>({
    queryKey: qk.projects,
    queryFn: fetchProjects,
    enabled: open,
  });
  const addedRepos = useMemo(
    () => new Set((projectsQuery.data ?? []).map((p) => p.repoFullName)),
    [projectsQuery.data],
  );

  const repos = useMemo(() => reposQuery.data ?? [], [reposQuery.data]);
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = q
      ? repos.filter((r) => r.fullName.toLowerCase().includes(q))
      : repos;
    return list.slice(0, 200);
  }, [repos, filter]);

  const reset = () => {
    setFilter('');
    setSelected(null);
  };

  const mutation = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('No repository selected');
      // Repo-only: the server derives name + default branch from GitHub.
      return createProject({ repoUrl: selected.fullName });
    },
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: qk.projects });
      // Refresh the cross-workspace feeds (Projects/Overview) too.
      queryClient.invalidateQueries({ queryKey: qk.me });
      reset();
      onCreated?.(project);
      onClose();
    },
  });

  // No repos to pick from: missing token, an API error, or an empty account.
  const cannotPick =
    reposQuery.isError || (!reposQuery.isLoading && repos.length === 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => mutation.mutate()}
            disabled={!selected || mutation.isPending || repoProjectsBlocked}
          >
            {mutation.isPending && <Spinner />}
            Create project
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {/* Which workspace this project is created in — re-scopes the repo list. */}
        <WorkspaceSelectField capability="project:manage" active={open} />

        {repoProjectsBlocked ? (
          <div className="rounded-lg border border-line bg-surface-1/40 p-4 text-sm">
            <p className="text-warning/90">
              Repo projects aren’t included on this workspace’s{' '}
              <strong>{entitlements?.plan}</strong> plan — it’s set up for prototypes.
            </p>
            <p className="mt-1 text-xs text-ink-muted">
              Create a prototype instead, or ask the platform operator to move this workspace
              to a plan that includes repo projects.
            </p>
          </div>
        ) : cannotPick ? (
          <div className="rounded-lg border border-line bg-surface-1/40 p-4 text-sm">
            <p className="text-warning/90">
              {reposQuery.isError
                ? errorMessage(reposQuery.error, "Couldn't load your repositories.")
                : 'No repositories found for the configured GitHub token.'}
            </p>
            <p className="mt-1 text-xs text-ink-muted">
              Add a GitHub token (with access to the repos you want) under
              Settings, then reopen this dialog to pick one.
            </p>
            <Link to="/settings" onClick={onClose} className="btn-secondary mt-3 inline-flex">
              Go to Settings
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <label className="label mb-0" htmlFor="p-filter">
                Select a repository
              </label>
              {reposQuery.isFetching && <Spinner className="h-3.5 w-3.5" />}
            </div>

            <input
              id="p-filter"
              className="input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={
                reposQuery.isLoading
                  ? 'Loading your repositories…'
                  : `Filter ${repos.length} repositories…`
              }
              disabled={reposQuery.isLoading}
              autoFocus
            />

            <div className="max-h-72 overflow-y-auto rounded-lg border border-line bg-surface-1/40">
              {filtered.map((r) => {
                const isSelected = selected?.fullName === r.fullName;
                const added = addedRepos.has(r.fullName);
                return (
                  <button
                    key={r.fullName}
                    type="button"
                    disabled={added}
                    onClick={() => !added && setSelected(r)}
                    title={added ? 'Already added as a project' : undefined}
                    className={`flex w-full items-center justify-between gap-3 border-b border-line/60 px-3 py-2 text-left text-sm last:border-b-0 ${
                      added
                        ? 'cursor-not-allowed text-ink-faint'
                        : isSelected
                          ? 'bg-signal/10 text-signal'
                          : 'text-ink hover:bg-surface-2/60'
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate font-mono">
                      {r.fullName}
                      {r.private && (
                        <span className="ml-1.5 text-2xs text-ink-faint">private</span>
                      )}
                    </span>
                    <span className="shrink-0 text-2xs text-ink-faint">
                      {added ? 'added' : r.defaultBranch}
                      {!added && !r.canPush && ' · read-only'}
                    </span>
                    <span className="w-4 shrink-0 text-signal">
                      {isSelected && !added ? (
                        <Check className="h-4 w-4" aria-hidden="true" />
                      ) : null}
                    </span>
                  </button>
                );
              })}
              {!reposQuery.isLoading && filtered.length === 0 && (
                <p className="px-3 py-3 text-xs text-ink-faint">
                  No repositories match “{filter.trim()}”.
                </p>
              )}
            </div>

            {selected && (
              <div className="space-y-1 text-xs">
                <p className="text-ink-muted">
                  Creates project{' '}
                  <strong className="text-ink">{selected.name}</strong>{' '}
                  tracking{' '}
                  <span className="font-mono text-ink-muted">{selected.fullName}</span>{' '}
                  · branch{' '}
                  <span className="font-mono text-ink-muted">{selected.defaultBranch}</span>.
                  <span className="text-ink-faint"> Both editable later in project settings.</span>
                </p>
                {!selected.canPush && (
                  <p className="flex gap-1.5 text-xs text-warning">
                    <AlertTriangle
                      className="mt-0.5 h-3 w-3 shrink-0"
                      aria-hidden="true"
                    />
                    <span>
                      GitHub reports your account has no push access to this
                      repo — runs will fail to push the branch / open a PR
                      unless your token is a classic PAT with the{' '}
                      <code>repo</code> scope.
                    </span>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {mutation.isError && (
          <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
            {errorMessage(mutation.error, 'Could not create project')}
          </div>
        )}
      </div>
    </Modal>
  );
}
